import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { API_ENDPOINTS } from '../api';

const OffersContext = createContext(null);

export function OffersProvider({ children }) {
  // OffersProvider wraps every route, but the global offers list + featured
  // banner are only ever rendered on "/" and "/offers". Every other page
  // (category, store, detail, blog) was still firing both fetches on every
  // load/refresh for data it never displays — wasted calls, visible in the
  // network tab and adding to the API's request volume for nothing. Track
  // the route reactively so this applies on every navigation, not just the
  // page OffersProvider happened to mount on.
  const { pathname } = useLocation();
  const rendersGlobalList = pathname === '/' || pathname === '/offers';
  // Offers list state (preserved across route changes)
  const [items, setItems] = useState([]);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [loadingOffers, setLoadingOffers] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [offersError, setOffersError] = useState(null);

  // Featured banner state
  const [trendingItems, setTrendingItems] = useState([]);
  const [loadingFeatured, setLoadingFeatured] = useState(true);

  // Guards against duplicate/overlapping fetches — e.g. the IntersectionObserver
  // can fire multiple times before `loadingMore` state has re-rendered, which
  // would otherwise trigger two requests for the same page and duplicate items.
  const fetchInFlight = useRef(false);

  // Featured banner is fetched once per app session, not on every visit back
  // to "/" — items don't change fast enough to justify refetching every nav.
  const featuredFetched = useRef(false);

  const fetchOffers = useCallback(async (category, searchQuery, lek, replace = false) => {
    // Only guard "load more" calls — a replace (new filter/search) should
    // always be allowed through immediately, even if a load-more is in flight.
    if (fetchInFlight.current && !replace) return;
    fetchInFlight.current = true;
    if (replace) setLoadingOffers(true); else setLoadingMore(true);
    setOffersError(null);
    try {
      const params = new URLSearchParams({ limit: 6 });
      if (category !== 'All') params.set('category', category);
      if (searchQuery) params.set('search', searchQuery);
      if (lek) params.set('lastEvaluatedKey', JSON.stringify(lek));
      const response = await fetch(`${API_ENDPOINTS.OFFERS}?${params}`);
      if (!response.ok) throw new Error('Failed to fetch offers');
      const data = await response.json();
      const newItems = data.offers || data;
      setItems(prev => {
        if (replace) {
          // Dedupe defensively even on a fresh replace — protects against the
          // backend ever returning the same offer twice in one page, which
          // would otherwise trigger React's duplicate-key warning.
          // Only dedupe items with a real (truthy) id — never collapse items
          // that are missing an id, or all of them would wrongly merge into one.
          const seen = new Set();
          return newItems.filter(i => {
            if (!i) return false;
            if (i.id == null) return true;
            if (seen.has(i.id)) return false;
            seen.add(i.id);
            return true;
          });
        }
        // De-duplicate by id in case the API returns overlapping pages or this
        // fetch raced with another one for the same cursor. Same safeguard:
        // never treat missing ids as duplicates of each other.
        const seen = new Set(prev.filter(i => i.id != null).map(i => i.id));
        const deduped = newItems.filter(i => i.id == null || !seen.has(i.id));
        return [...prev, ...deduped];
      });
      setLastEvaluatedKey(data.lastEvaluatedKey || null);
      setHasMore(!!data.lastEvaluatedKey);
    } catch (err) {
      setOffersError(err.message);
    } finally {
      if (replace) setLoadingOffers(false); else setLoadingMore(false);
      fetchInFlight.current = false;
    }
  }, []);

  // Refetch when filter or active search changes (single source of truth).
  // Also scroll to the top of the grid so users see the new results, not a mid-page void.
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (!rendersGlobalList) {
      setLoadingOffers(false);
      isInitialMount.current = false;
      return;
    }
    fetchOffers(filter, activeSearch, null, true);
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [filter, activeSearch, fetchOffers, rendersGlobalList]);

  // Featured banner: only rendered on the home page.
  useEffect(() => {
    if (pathname !== '/' || featuredFetched.current) {
      setLoadingFeatured(false);
      return;
    }
    featuredFetched.current = true;
    let cancelled = false;
    async function fetchFeatured() {
      setLoadingFeatured(true);
      try {
        const response = await fetch(API_ENDPOINTS.FEATURED_OFFERS);
        if (response.ok) {
          const data = await response.json();
          const rawFeatured = data.featuredOffers || data;
          // De-duplicate by a truly unique key. NOTE: the backend's `id` field
          // is NOT guaranteed unique for featured offers (multiple entries can
          // share the same `id` while having distinct DynamoDB sort keys `sk`).
          // Dedupe on `sk` (or array index as a last-resort fallback) instead,
          // and give each item a `_uid` to use as the React list key so
          // duplicate `id`s never cause React's duplicate-key warning or
          // collapse distinct slides into one.
          const seen = new Set();
          const deduped = (rawFeatured || []).reduce((acc, item, idx) => {
            if (!item) return acc;
            const uniqueKey = item.sk || item.id || `idx-${idx}`;
            if (seen.has(uniqueKey)) return acc;
            seen.add(uniqueKey);
            acc.push({ ...item, _uid: uniqueKey });
            return acc;
          }, []);
          if (!cancelled) setTrendingItems(deduped);
        }
      } catch (err) {
        console.error('Failed to fetch featured offers:', err);
      } finally {
        if (!cancelled) setLoadingFeatured(false);
      }
    }
    fetchFeatured();
    return () => { cancelled = true; };
  }, [pathname]);

  return (
    <OffersContext.Provider value={{
      items, lastEvaluatedKey, hasMore, filter, search, activeSearch,
      loadingOffers, loadingMore, offersError,
      trendingItems, loadingFeatured,
      setFilter, setSearch, setActiveSearch,
      setItems, setLastEvaluatedKey, setHasMore,
      fetchOffers,
    }}>
      {children}
    </OffersContext.Provider>
  );
}

export function useOffers() {
  const ctx = useContext(OffersContext);
  if (!ctx) throw new Error('useOffers must be used within OffersProvider');
  return ctx;
}
